import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import type { CreateCustomerInput, Customer } from '@catalyst-psa/shared';
import { NET_TERMS_OPTIONS } from '@catalyst-psa/shared';

import { ApiError, mutationError } from '../../api/client.js';
import { useAuth } from '../../auth/authContext.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { CheckboxField, SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { MultiSelectSearch } from '../../components/ui/MultiSelectSearch.js';
import { HistoryPanel } from '../audit/HistoryPanel.js';
import { ContactsSection } from '../contacts/ContactsSection.js';
import { ContactPortalTab } from '../contacts/ContactPortalTab.js';
import { useAgreements } from '../agreements/api.js';
import { OnboardingRecordsTab } from '../onboarding/OnboardingRecordsTab.js';
import {
  CustomerAgreementsTab,
  CustomerInvoicesTab,
  CustomerOpportunitiesTab,
  CustomerProjectsTab,
  CustomerTicketsTab,
  CustomerTimeEntriesTab,
} from './CustomerRelatedTabs.js';
import { CustomerFinanceTab } from './CustomerFinanceTab.js';
import {
  useCreateCustomer,
  useCustomerFormMemberOptions,
  useCustomerFormTypeOptions,
  useDeleteCustomer,
  useUpdateCustomer,
} from './api.js';

interface CustomerFormModalProps {
  isOpen: boolean;
  /** Existing customer to edit, or null to create a new one. */
  customer: Customer | null;
  onClose: () => void;
  /** Tab to open when modal opens (used for backward navigation) */
  initialTab?: string;
  /** Extra buttons rendered in the modal header (e.g. “Back to onboarding”). */
  headerActions?: ReactNode;
}

type CustomerTab =
  | 'details'
  | 'contacts'
  | 'portal'
  | 'team'
  | 'agreements'
  | 'opportunities'
  | 'onboarding'
  | 'projects'
  | 'tickets'
  | 'time'
  | 'billing'
  | 'invoices'
  | 'finance'
  | 'other'
  | 'history';

const CUSTOMER_TABS: Array<{ id: CustomerTab; label: string }> = [
  { id: 'details', label: 'Details' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'portal', label: 'Portal' },
  { id: 'team', label: 'Team' },
  { id: 'projects', label: 'Projects' },
  { id: 'tickets', label: 'Tickets' },
  { id: 'agreements', label: 'Agreements' },
  { id: 'opportunities', label: 'Opportunities' },
  { id: 'onboarding', label: 'Onboarding' },
  { id: 'time', label: 'Time entries' },
  { id: 'billing', label: 'Billing' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'finance', label: 'Finance' },
  { id: 'other', label: 'Other' },
  { id: 'history', label: 'History' },
];

/**
 * Tabs gated by sidebar-menu permissions. A member only sees a mapped tab when they can see
 * at least one of the corresponding menu items; unmapped tabs (details, contacts, team,
 * other, history) are always visible.
 */
const TAB_MENU_KEYS: Partial<Record<CustomerTab, string[]>> = {
  projects: ['projects'],
  tickets: ['support-tickets', 'project-tickets'],
  agreements: ['agreements'],
  opportunities: ['opportunities'],
  onboarding: ['onboarding'],
  time: ['time-entries'],
  billing: ['invoices'],
  invoices: ['invoices'],
  finance: ['invoices'],
};

interface FormState {
  name: string;
  aka: string;
  notes: string;
  netsuiteAccountId: string;
  netsuiteAccountName: string;
  qboCustomerId: string;
  changeControlled: boolean;
  loeBeforeWork: boolean;
  marketingAcquisition: boolean;
  verboseHourNotesRequired: boolean;
  documentBugDetail: boolean;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
  website: string;
  billingCcEmails: string;
  netsuiteStatus: string;
  goLiveDate: string;
  groupEmail: string;
  netTerms: string;
  billingReminder: string;
  defaultSupportAgreementId: string;
  typeIds: string[];
  primarySalesRepIds: string[];
  primaryFunctionalIds: string[];
  primaryProjectManagerIds: string[];
  primaryTechnicalIds: string[];
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  aka: '',
  notes: '',
  netsuiteAccountId: '',
  netsuiteAccountName: '',
  qboCustomerId: '',
  changeControlled: false,
  loeBeforeWork: false,
  marketingAcquisition: false,
  verboseHourNotesRequired: false,
  documentBugDetail: false,
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  phone: '',
  website: '',
  billingCcEmails: '',
  netsuiteStatus: '',
  goLiveDate: '',
  groupEmail: '',
  netTerms: 'Net 30',
  billingReminder: '',
  defaultSupportAgreementId: '',
  typeIds: [],
  primarySalesRepIds: [],
  primaryFunctionalIds: [],
  primaryProjectManagerIds: [],
  primaryTechnicalIds: [],
  isActive: true,
};

function toFormState(customer: Customer | null): FormState {
  if (!customer) {
    return EMPTY_FORM;
  }
  return {
    name: customer.name,
    aka: customer.aka ?? '',
    notes: customer.notes ?? '',
    netsuiteAccountId: customer.netsuiteAccountId ?? '',
    qboCustomerId: customer.qboCustomerId ?? '',
    changeControlled: customer.changeControlled,
    loeBeforeWork: customer.loeBeforeWork,
    marketingAcquisition: customer.marketingAcquisition,
    verboseHourNotesRequired: customer.verboseHourNotesRequired,
    documentBugDetail: customer.documentBugDetail,
    addressLine1: customer.addressLine1 ?? '',
    addressLine2: customer.addressLine2 ?? '',
    city: customer.city ?? '',
    state: customer.state ?? '',
    postalCode: customer.postalCode ?? '',
    phone: customer.phone ?? '',
    website: customer.website ?? '',
    billingCcEmails: customer.billingCcEmails ?? '',
    netsuiteStatus: customer.netsuiteStatus ?? '',
    netsuiteAccountName: customer.netsuiteAccountName ?? '',
    goLiveDate: customer.goLiveDate ?? '',
    groupEmail: customer.groupEmail ?? '',
    netTerms: customer.netTerms ?? 'Net 30',
    billingReminder: customer.billingReminder ?? '',
    defaultSupportAgreementId: customer.defaultSupportAgreementId ?? '',
    typeIds: customer.typeIds,
    primarySalesRepIds: customer.primarySalesRepIds,
    primaryFunctionalIds: customer.primaryFunctionalIds,
    primaryProjectManagerIds: customer.primaryProjectManagerIds,
    primaryTechnicalIds: customer.primaryTechnicalIds,
    isActive: customer.isActive,
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

export function CustomerFormModal({
  isOpen,
  customer: customerProp,
  onClose,
  initialTab,
  headerActions,
}: CustomerFormModalProps): JSX.Element {
  const [customer, setCustomer] = useState<Customer | null>(customerProp);
  const isEdit = customer !== null;
  const { isAdmin, can, canSeeMenu } = useAuth();
  const canEdit = can('customers', 'edit');
  const saveMode = useSaveMode();
  const [form, setForm] = useState<FormState>(() => toFormState(customer));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [tab, setTab] = useState<CustomerTab>((initialTab as CustomerTab) ?? 'details');

  // Hide tabs whose mapped sidebar-menu permissions the member lacks.
  const canSeeTab = useCallback(
    (id: CustomerTab): boolean => {
      const keys = TAB_MENU_KEYS[id];
      return !keys || keys.some((key) => canSeeMenu(key));
    },
    [canSeeMenu],
  );
  const visibleTabs = useMemo(
    () => CUSTOMER_TABS.filter((t) => canSeeTab(t.id)),
    [canSeeTab],
  );

  const typeOptionsQuery = useCustomerFormTypeOptions();
  const typeOptions = typeOptionsQuery.data ?? [];
  const agreementOptionsQuery = useAgreements(
    {
      page: 1,
      pageSize: 200,
      customerId: customer?.id,
      sortBy: 'name',
      sortDir: 'asc',
    },
    {
      enabled: isOpen && isEdit,
    },
  );
  const agreementOptions = agreementOptionsQuery.data?.data ?? [];

  // Active members (with role flags) used to populate the primary-member pickers.
  const membersQuery = useCustomerFormMemberOptions();
  const activeMembers = membersQuery.data ?? [];
  // Role-flagged options, but always keep already-selected members so edits never lose them.
  const memberOptionsFor = (
    flag: 'isSalesRep' | 'isFunctional' | 'isProjectManager' | 'isTechnical',
    selectedIds: string[],
  ): { id: string; name: string }[] =>
    activeMembers
      .filter((m) => m[flag] || selectedIds.includes(m.id))
      .map((m) => ({ id: m.id, name: m.displayName }));

  const createMutation = useCreateCustomer();
  const updateMutation = useUpdateCustomer();
  const deleteMutation = useDeleteCustomer();
  const mutation = isEdit ? updateMutation : createMutation;

  const resetForm = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetSaveMode = saveMode.reset;

  useEffect(() => {
    if (isOpen) {
      setCustomer(customerProp);
    }
  }, [isOpen, customerProp]);

  useEffect(() => {
    if (isOpen) {
      setForm(toFormState(customer));
      setConfirmOpen(false);
      const desired = (initialTab as CustomerTab) ?? 'details';
      setTab(canSeeTab(desired) ? desired : 'details');
      resetSaveMode();
      resetForm();
      resetUpdate();
    }
  }, [isOpen, customer, initialTab, canSeeTab, resetSaveMode, resetForm, resetUpdate]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save customer.'
        : null;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const trimOrNull = (value: string): string | null => (value.trim() ? value.trim() : null);
    const payload: CreateCustomerInput = {
      name: form.name.trim(),
      aka: trimOrNull(form.aka),
      notes: trimOrNull(form.notes),
      netsuiteAccountId: trimOrNull(form.netsuiteAccountId),
      qboCustomerId: trimOrNull(form.qboCustomerId),
      changeControlled: form.changeControlled,
      loeBeforeWork: form.loeBeforeWork,
      marketingAcquisition: form.marketingAcquisition,
      verboseHourNotesRequired: form.verboseHourNotesRequired,
      documentBugDetail: form.documentBugDetail,
      addressLine1: trimOrNull(form.addressLine1),
      addressLine2: trimOrNull(form.addressLine2),
      city: trimOrNull(form.city),
      state: trimOrNull(form.state),
      postalCode: trimOrNull(form.postalCode),
      phone: trimOrNull(form.phone),
      website: trimOrNull(form.website),
      billingCcEmails: trimOrNull(form.billingCcEmails),
      netsuiteStatus: trimOrNull(form.netsuiteStatus),
      netsuiteAccountName: trimOrNull(form.netsuiteAccountName),
      goLiveDate: form.goLiveDate || null,
      groupEmail: trimOrNull(form.groupEmail),
      netTerms: form.netTerms,
      billingReminder: trimOrNull(form.billingReminder),
      defaultSupportAgreementId: form.defaultSupportAgreementId || null,
      typeIds: form.typeIds,
      primarySalesRepIds: form.primarySalesRepIds,
      primaryFunctionalIds: form.primaryFunctionalIds,
      primaryProjectManagerIds: form.primaryProjectManagerIds,
      primaryTechnicalIds: form.primaryTechnicalIds,
      isActive: form.isActive,
    };

    if (isEdit) {
      updateMutation.mutate(
        { id: customer.id, input: payload },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          if (saveMode.closeAfterSaveRef.current) {
            onClose();
          } else {
            setCustomer(created);
          }
        },
      });
    }
  };

  const handleDelete = (): void => {
    if (!customer) {
      return;
    }
    deleteMutation.mutate(customer.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? `Edit customer — ${customer.name}` : 'New customer'}
        size="wide"
        fillHeight={isEdit}
        headerActions={headerActions}
        tabs={isEdit ? visibleTabs : undefined}
        activeTabId={tab}
        onTabChange={(id) => setTab(id as CustomerTab)}
        footer={
          <FormActions
            formId="customer-form"
            saveMode={saveMode}
            isPending={mutation.isPending}
            onCancel={onClose}
            leading={
              isEdit && isAdmin ? (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600 hover:bg-rose-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Delete
                </Button>
              ) : null
            }
          />
        }
      >
        <form
          id="customer-form"
          onSubmit={handleSubmit}
          className={`grid grid-cols-1 gap-4 md:grid-cols-2 ${
            isEdit && !['details', 'team', 'billing', 'other'].includes(tab) ? 'hidden' : ''
          }`}
        >
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 md:col-span-2">
              {generalError}
            </div>
          ) : null}

          {/* Details */}
          <div className={!isEdit || tab === 'details' ? 'contents' : 'hidden'}>
            <TextField
              label="Name"
              required
              value={form.name}
              maxLength={200}
              autoFocus
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              error={fieldErrors.name}
            />

            <TextField
              label="AKA"
              value={form.aka}
              maxLength={256}
              placeholder="Also-known-as / alias (searchable)"
              onChange={(e) => setForm((f) => ({ ...f, aka: e.target.value }))}
              error={fieldErrors.aka}
            />

            <div className="md:col-span-2">
              <span className="text-sm font-medium text-slate-700">Types</span>
              <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 rounded-lg border border-slate-200 p-3 sm:grid-cols-3 lg:grid-cols-4">
                {typeOptions.length === 0 ? (
                  <p className="col-span-full text-xs text-slate-400">
                    No customer types configured.
                  </p>
                ) : (
                  typeOptions.map((type) => (
                    <CheckboxField
                      key={type.id}
                      label={type.name}
                      checked={form.typeIds.includes(type.id)}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          typeIds: e.target.checked
                            ? [...f.typeIds, type.id]
                            : f.typeIds.filter((id) => id !== type.id),
                        }))
                      }
                    />
                  ))
                )}
              </div>
            </div>

            <TextField
              label="Phone"
              value={form.phone}
              maxLength={64}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              error={fieldErrors.phone}
            />
            <TextField
              label="Website"
              value={form.website}
              maxLength={256}
              placeholder="https://example.com"
              onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
              error={fieldErrors.website}
            />

            <TextField
              label="Address line 1"
              value={form.addressLine1}
              maxLength={256}
              onChange={(e) => setForm((f) => ({ ...f, addressLine1: e.target.value }))}
              error={fieldErrors.addressLine1}
            />
            <TextField
              label="Address line 2"
              value={form.addressLine2}
              maxLength={256}
              onChange={(e) => setForm((f) => ({ ...f, addressLine2: e.target.value }))}
              error={fieldErrors.addressLine2}
            />
            <div className="grid grid-cols-3 gap-4 md:col-span-2">
              <TextField
                label="City"
                value={form.city}
                maxLength={128}
                onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                error={fieldErrors.city}
              />
              <TextField
                label="State"
                value={form.state}
                maxLength={128}
                onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                error={fieldErrors.state}
              />
              <TextField
                label="Postal code"
                value={form.postalCode}
                maxLength={32}
                onChange={(e) => setForm((f) => ({ ...f, postalCode: e.target.value }))}
                error={fieldErrors.postalCode}
              />
            </div>

            <div className="md:col-span-2">
              <TextAreaField
                label="Notes"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                error={fieldErrors.notes}
              />
            </div>

            <div className="md:col-span-2">
              <span className="text-sm font-medium text-slate-700">ClickUp Support Tags</span>
              <p className="mt-0.5 text-xs text-slate-500">
                Support-delivery expectations for this customer. Each of these can be mapped to a
                ClickUp tag on the ClickUp integration page; when a ticket is created from a
                ClickUp task, the mapped tags for every ticked box are applied to that task.
              </p>
              <div className="mt-1 grid grid-cols-1 gap-x-4 gap-y-1 rounded-lg border border-slate-200 p-3 sm:grid-cols-2">
                <CheckboxField
                  label="Change controlled"
                  checked={form.changeControlled}
                  onChange={(e) => setForm((f) => ({ ...f, changeControlled: e.target.checked }))}
                />
                <CheckboxField
                  label="LOE before work"
                  checked={form.loeBeforeWork}
                  onChange={(e) => setForm((f) => ({ ...f, loeBeforeWork: e.target.checked }))}
                />
                <CheckboxField
                  label="Verbose hour entry notes required"
                  checked={form.verboseHourNotesRequired}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, verboseHourNotesRequired: e.target.checked }))
                  }
                />
                <CheckboxField
                  label="Document bug vs non-bug in detail"
                  checked={form.documentBugDetail}
                  onChange={(e) => setForm((f) => ({ ...f, documentBugDetail: e.target.checked }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 md:col-span-2 lg:grid-cols-4">
              <CheckboxField
                label="Marketing acquisition"
                checked={form.marketingAcquisition}
                onChange={(e) => setForm((f) => ({ ...f, marketingAcquisition: e.target.checked }))}
              />
              <CheckboxField
                label="Active"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
              />
            </div>
          </div>

          {/* Team */}
          <div
            className={
              !isEdit || tab === 'team'
                ? 'md:col-span-2 grid grid-cols-1 gap-4 sm:grid-cols-2'
                : 'hidden'
            }
          >
            <MultiSelectSearch
              label="Primary Sales Reps"
              options={memberOptionsFor('isSalesRep', form.primarySalesRepIds)}
              selected={form.primarySalesRepIds}
              emptyText="No sales reps flagged on members."
              placeholder="Search members…"
              onToggle={(id, checked) =>
                setForm((f) => ({
                  ...f,
                  primarySalesRepIds: checked
                    ? [...f.primarySalesRepIds, id]
                    : f.primarySalesRepIds.filter((x) => x !== id),
                }))
              }
            />
            <MultiSelectSearch
              label="Primary Functional"
              options={memberOptionsFor('isFunctional', form.primaryFunctionalIds)}
              selected={form.primaryFunctionalIds}
              emptyText="No functional resources flagged on members."
              placeholder="Search members…"
              onToggle={(id, checked) =>
                setForm((f) => ({
                  ...f,
                  primaryFunctionalIds: checked
                    ? [...f.primaryFunctionalIds, id]
                    : f.primaryFunctionalIds.filter((x) => x !== id),
                }))
              }
            />
            <MultiSelectSearch
              label="Primary Project Manager"
              options={memberOptionsFor('isProjectManager', form.primaryProjectManagerIds)}
              selected={form.primaryProjectManagerIds}
              emptyText="No project managers flagged on members."
              placeholder="Search members…"
              onToggle={(id, checked) =>
                setForm((f) => ({
                  ...f,
                  primaryProjectManagerIds: checked
                    ? [...f.primaryProjectManagerIds, id]
                    : f.primaryProjectManagerIds.filter((x) => x !== id),
                }))
              }
            />
            <MultiSelectSearch
              label="Primary Technical"
              options={memberOptionsFor('isTechnical', form.primaryTechnicalIds)}
              selected={form.primaryTechnicalIds}
              emptyText="No technical resources flagged on members."
              placeholder="Search members…"
              onToggle={(id, checked) =>
                setForm((f) => ({
                  ...f,
                  primaryTechnicalIds: checked
                    ? [...f.primaryTechnicalIds, id]
                    : f.primaryTechnicalIds.filter((x) => x !== id),
                }))
              }
            />
          </div>

          {/* Billing */}
          <div className={!isEdit || tab === 'billing' ? 'contents' : 'hidden'}>
            <div>
              <TextField
                label="QuickBooks ID"
                value={form.qboCustomerId}
                maxLength={64}
                onChange={(e) => setForm((f) => ({ ...f, qboCustomerId: e.target.value }))}
                error={fieldErrors.qboCustomerId}
              />
              {form.qboCustomerId.trim() && (
                <a
                  href={`https://qbo.intuit.com/app/customerdetail?nameId=${encodeURIComponent(
                    form.qboCustomerId.trim(),
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-block text-sm text-brand-600 hover:underline"
                >
                  Open in QuickBooks ↗
                </a>
              )}
            </div>
            <div className="md:col-span-2">
              <TextAreaField
                label="Billing CC emails"
                rows={2}
                value={form.billingCcEmails}
                placeholder="Separate multiple emails with commas"
                onChange={(e) => setForm((f) => ({ ...f, billingCcEmails: e.target.value }))}
                error={fieldErrors.billingCcEmails}
              />
            </div>

            <SelectField
              label="Net Terms"
              value={form.netTerms}
              options={[
                ...NET_TERMS_OPTIONS.map((value) => ({ value, label: value })),
                ...(form.netTerms && !NET_TERMS_OPTIONS.includes(form.netTerms as never)
                  ? [{ value: form.netTerms, label: `${form.netTerms} (synced)` }]
                  : []),
              ]}
              onChange={(e) => setForm((f) => ({ ...f, netTerms: e.target.value }))}
              error={fieldErrors.netTerms}
            />

            <TextField
              label="Billing reminder"
              value={form.billingReminder}
              maxLength={256}
              placeholder="Shown on generate invoices; copied to each invoice's internal notes"
              onChange={(e) => setForm((f) => ({ ...f, billingReminder: e.target.value }))}
              error={fieldErrors.billingReminder}
            />

            <SelectField
              label="Default Support Agreement"
              value={form.defaultSupportAgreementId}
              disabled={!isEdit}
              placeholder={
                !isEdit
                  ? 'Save customer first to choose default agreement'
                  : agreementOptionsQuery.isLoading
                    ? 'Loading...'
                    : 'None'
              }
              options={[
                { value: '', label: 'None' },
                ...agreementOptions.map((agreement) => ({
                  value: agreement.id,
                  label: `#${agreement.number} - ${agreement.name}`,
                })),
              ]}
              onChange={(e) =>
                setForm((f) => ({ ...f, defaultSupportAgreementId: e.target.value }))
              }
              error={fieldErrors.defaultSupportAgreementId}
            />
          </div>

          {/* Other */}
          <div className={!isEdit || tab === 'other' ? 'contents' : 'hidden'}>
            <TextField
              label="Group Email"
              value={form.groupEmail}
              maxLength={256}
              placeholder="Set automatically when the MS group is created"
              onChange={(e) => setForm((f) => ({ ...f, groupEmail: e.target.value }))}
              error={fieldErrors.groupEmail}
            />
            <TextField
              label="NetSuite Account ID"
              value={form.netsuiteAccountId}
              maxLength={128}
              onChange={(e) => setForm((f) => ({ ...f, netsuiteAccountId: e.target.value }))}
              error={fieldErrors.netsuiteAccountId}
            />
            <TextField
              label="NetSuite Account Name"
              value={form.netsuiteAccountName}
              maxLength={256}
              placeholder="Searchable on the customers page"
              onChange={(e) => setForm((f) => ({ ...f, netsuiteAccountName: e.target.value }))}
              error={fieldErrors.netsuiteAccountName}
            />
            <TextField
              label="NetSuite status"
              value={form.netsuiteStatus}
              maxLength={128}
              onChange={(e) => setForm((f) => ({ ...f, netsuiteStatus: e.target.value }))}
              error={fieldErrors.netsuiteStatus}
            />
            <TextField
              label="Go-live date"
              type="date"
              value={form.goLiveDate}
              onChange={(e) => setForm((f) => ({ ...f, goLiveDate: e.target.value }))}
              error={fieldErrors.goLiveDate}
            />
          </div>
        </form>
        {isEdit && customer ? (
          <div className={tab === 'contacts' ? '' : 'hidden'}>
            <ContactsSection customerId={customer.id} canEdit={canEdit} />
          </div>
        ) : null}
        {isEdit && customer && tab === 'agreements' ? (
          <CustomerAgreementsTab customerId={customer.id} />
        ) : null}        {isEdit && customer && tab === 'opportunities' ? (
          <CustomerOpportunitiesTab customerId={customer.id} />
        ) : null}
        {isEdit && customer && tab === 'onboarding' ? (
          <OnboardingRecordsTab customerId={customer.id} />
        ) : null}
        {isEdit && customer && tab === 'projects' ? (
          <CustomerProjectsTab customerId={customer.id} />
        ) : null}
        {isEdit && customer && tab === 'tickets' ? (
          <CustomerTicketsTab customerId={customer.id} />
        ) : null}
        {isEdit && customer && tab === 'time' ? (
          <CustomerTimeEntriesTab customerId={customer.id} />
        ) : null}
        {isEdit && customer && tab === 'invoices' ? (
          <CustomerInvoicesTab customerId={customer.id} />
        ) : null}
        {isEdit && customer && tab === 'finance' ? (
          <CustomerFinanceTab customerId={customer.id} />
        ) : null}
        {isEdit && customer && tab === 'history' ? (
          <HistoryPanel entityType="Customer" entityId={customer.id} />
        ) : null}
        {isEdit && customer && tab === 'portal' ? (
          <ContactPortalTab customerId={customer.id} />
        ) : null}
      </Modal>
      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete customer"
        message={`Delete "${customer?.name ?? ''}"? This action cannot be undone.`}
        error={mutationError(deleteMutation.error, 'Failed to delete the customer.')}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
